// Default fallback rendered by ErrorBoundary when no custom `fallback`
// prop is supplied. Lives in its own module so the boundary file
// stays a single-component file (react-refresh is happier when class
// components and helper functions don't share a module — and the
// linter enforces that with `react-refresh/only-export-components`).

interface ErrorBoundaryFallbackProps {
  error: Error;
  scopeLabel?: string;
  onReset: () => void;
}

export default function ErrorBoundaryFallback({
  error,
  scopeLabel,
  onReset,
}: ErrorBoundaryFallbackProps) {
  const heading = scopeLabel
    ? `Something went wrong in ${scopeLabel}.`
    : "Something went wrong.";
  return (
    <div
      role="alert"
      data-testid="error-boundary-fallback"
      className="max-w-2xl mx-auto my-12 rounded-lg border border-red-200 bg-red-50 p-6 dark:border-red-900/50 dark:bg-red-950/20"
    >
      <h2 className="text-h3 text-red-900 dark:text-red-200">{heading}</h2>
      <p className="mt-2 text-body text-slate-700 dark:text-slate-300">
        We hit an unexpected error rendering this page. The rest of the app
        is still usable — try going back, reloading, or navigating somewhere
        else. If this keeps happening, copy the technical details below and
        share them with support.
      </p>
      <div className="mt-4 flex flex-wrap gap-3">
        <button
          type="button"
          onClick={onReset}
          className="btn-primary"
          data-testid="error-boundary-retry"
        >
          Try again
        </button>
        <button
          type="button"
          onClick={() => window.history.back()}
          className="btn-secondary"
        >
          Go back
        </button>
        <button
          type="button"
          onClick={() => window.location.reload()}
          className="btn-secondary"
        >
          Reload the app
        </button>
      </div>
      <details
        data-testid="error-boundary-details"
        className="mt-6 text-caption text-slate-600 dark:text-slate-400"
      >
        <summary className="cursor-pointer select-none">
          Technical details
        </summary>
        <pre className="mt-2 whitespace-pre-wrap break-words rounded-md bg-white p-3 font-mono text-caption text-slate-800 dark:bg-slate-900 dark:text-slate-200">
          {error.message}
          {error.stack ? `\n\n${error.stack}` : ""}
        </pre>
      </details>
    </div>
  );
}
